import type { TFunction } from 'i18next';

/**
 * Maps a DEV-6905 unified status value (DRAFT, ABANDONED, SYSTEM_PROCESSING,
 * CRM_STATUS_*, RESUMPTION_*, and pre-DEV-6905 legacy values) to the i18n key
 * suffix consumed by {@link getClaimStatusLabel}.
 *
 * Returns the *short* key (e.g. `'draft'`); the i18next call site composes
 * the full key (`home.status.draft`). This keeps the helper free of
 * `t()` calls (project rule: utilities return keys, components translate).
 *
 * @param status Raw status string from the BFF (case-insensitive).
 * @returns Short label key, or `'unknown'` for null / unrecognised input.
 */
export function getStatusLabelKey(status: string | null | undefined): string {
  const normalized = (status || '').toUpperCase();

  switch (normalized) {
    case 'DRAFT':
    case 'RESUMPTION_DRAFT':
      return 'draft';

    case 'ABANDONED':
    case 'RESUMPTION_ABANDONED':
    case 'CANCELLED':
    case 'CRM_STATUS_CANCELLED':
    case 'RESUMPTION_CRM_STATUS_CANCELLED':
      return 'cancelled';

    // DEV-7217 — DELETED items are hidden by the BFF filter; this is a safety net
    // so a leaked item renders a distinct label instead of "unknown", making the
    // upstream regression easy to spot.
    case 'DELETED':
    case 'RESUMPTION_DELETED':
      return 'deleted';

    case 'SYSTEM_PROCESSING':
    case 'RESUMPTION_SYSTEM_PROCESSING':
      return 'systemProcessing';

    case 'NEW':
    case 'SUBMITTED':
    case 'CRM_STATUS_NEW':
    case 'RESUMPTION_CRM_STATUS_NEW':
    case 'CRM_STATUS_SUBMITTED':
    case 'RESUMPTION_CRM_STATUS_SUBMITTED':
      return 'submitted';

    case 'IN_PROGRESS':
    case 'IN_REVISION':
    case 'REVISION_COMPLETED':
    case 'OPEN':
    case 'CRM_STATUS_IN_PROGRESS':
    case 'RESUMPTION_CRM_STATUS_IN_PROGRESS':
    case 'CRM_STATUS_IN_REVISION':
    case 'RESUMPTION_CRM_STATUS_IN_REVISION':
    case 'CRM_STATUS_REVISION_COMPLETED':
    case 'RESUMPTION_CRM_STATUS_REVISION_COMPLETED':
    case 'CRM_STATUS_OPEN':
    case 'RESUMPTION_CRM_STATUS_OPEN':
      return 'underReview';

    case 'TO_BE_COMPLETED':
    case 'INFORMATION_REQUESTED':
    case 'CRM_STATUS_TO_BE_COMPLETED':
    case 'RESUMPTION_CRM_STATUS_TO_BE_COMPLETED':
    case 'CRM_STATUS_INFORMATION_REQUESTED':
    case 'RESUMPTION_CRM_STATUS_INFORMATION_REQUESTED':
      return 'documentsRequired';

    case 'COMPLETED':
    case 'CRM_STATUS_COMPLETED':
    case 'RESUMPTION_CRM_STATUS_COMPLETED':
      return 'processed';

    case 'CLOSED':
    case 'CRM_STATUS_CLOSED':
    case 'RESUMPTION_CRM_STATUS_CLOSED':
      return 'awaitingInformation';

    default:
      // ERROR (BFF filters these — defensive fallback), unknown values, null/empty.
      return 'unknown';
  }
}

/**
 * Get translated display text for a claim status.
 * Shared across HomeScreen, EventSelectionScreen, and DuplicateClaimModal.
 *
 * @param status Raw status string from backend
 * @param t i18next translation function
 * @param fallback Optional fallback for unknown statuses (default: translated "unknown")
 */
export function getClaimStatusLabel(
  status: string | null | undefined,
  t: TFunction,
  fallback?: string
): string {
  const key = getStatusLabelKey(status);
  if (key === 'unknown') {
    return fallback ?? t('home.status.unknown', 'Inconnu');
  }
  return t(`home.status.${key}`);
}

/**
 * True when the claim is in the SYSTEM_PROCESSING transient (Portal or Resumption
 * origin) — covers both `SYSTEM_PROCESSING` and `RESUMPTION_SYSTEM_PROCESSING`.
 * DEV-6905 — replaces the previous `claim.isSystemProcessing` boolean.
 */
export function isSystemProcessingStatus(status: string | null | undefined): boolean {
  return getStatusLabelKey(status) === 'systemProcessing';
}

/**
 * True when the claim status indicates a system-driven DELETED submission.
 * DEV-7217 — defense-in-depth filter for the home list; the BFF should never return
 * these, but rendering must hide them if a regression slips through.
 */
export function isDeletedStatus(status: string | null | undefined): boolean {
  return getStatusLabelKey(status) === 'deleted';
}
